import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../supabase";

// Shared components from ServiceSelector
import FormContainer from "../ServiceSelector/shared/FormContainer";

// Step components
import ProfileBasics from "./steps/ProfileBasics";
import QualificationsExperience from "./steps/QualificationsExperience";
import ChurchAffiliation from "./steps/ChurchAffiliation";
import WaysToHelp from "./steps/WaysToHelp";
import ChurchReference from "./steps/ChurchReference";
import ProfileCelebration from "./steps/ProfileCelebration";

const STORAGE_KEY = "serveToThrive_formData";
const STEP_STORAGE_KEY = "serveToThrive_currentStep";

export default function ServeToThrive() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editMode = searchParams.get("edit") === "true";

  // Steps: 'profile' -> 'qualifications' -> 'church' -> 'waysToHelp' -> 'celebration'
  const [currentStep, setCurrentStep] = useState("profile");
  const [loading, setLoading] = useState(true); // Always load initially to check for existing data
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Profile form data - try to restore from localStorage first
  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        return JSON.parse(savedData);
      } catch (e) {
        console.error("Error parsing saved form data:", e);
      }
    }
    return {
      // Step 1: Profile Basics
      name: "",
      location: "", // city/town
      profilePictureUrl: "",
      skillTitle: "", // Short bio / service title

      // Step 2: Qualifications & Experience
      bio: "", // About you (longer bio)
      qualifications: "",
      experience: "",

      // Step 3: Church Affiliation
      churchAffiliation: "affiliated", // 'affiliated' or 'independent'
      churchName: "",
      churchCity: "",
      denomination: "",
      churchRole: "",

      // Step 4: Ways to Help
      waysToHelp: [], // Array of selected help options

      // Step 5: Church Reference
      refereeName: "",
      refereeEmail: "",
      refereePhone: "",
      refereeRole: "",
    };
  });

  // Auto-save debounce
  const [saveTimeout, setSaveTimeout] = useState(null);

  // Load existing profile from Supabase on mount
  useEffect(() => {
    loadExistingProfile(editMode);
  }, [editMode]);

  // Save to localStorage whenever formData changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  // Restore step from localStorage OR force profile step when in edit mode
  useEffect(() => {
    if (editMode) {
      // In edit mode, always start from profile
      setCurrentStep("profile");
    } else {
      // Normal mode: restore from localStorage if available
      const savedStep = localStorage.getItem(STEP_STORAGE_KEY);
      if (savedStep) {
        setCurrentStep(savedStep);
      }
    }
  }, [editMode]);

  // Save current step to localStorage (but not when in edit mode)
  useEffect(() => {
    if (!editMode && currentStep) {
      localStorage.setItem(STEP_STORAGE_KEY, currentStep);
    }
  }, [currentStep, editMode]);

  // Auto-save to Supabase effect
  useEffect(() => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    const timeout = setTimeout(() => {
      if (formData.name || formData.location || formData.qualifications) {
        autoSave();
      }
    }, 3000); // Auto-save after 3 seconds of inactivity

    setSaveTimeout(timeout);

    return () => {
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
    };
  }, [formData]);

  const loadExistingProfile = async (isEditMode) => {
    try {
      setLoading(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        alert("You must be logged in");
        window.location.href = "/";
        return;
      }

      // Try to load profile from Supabase
      const { data: profile, error: profileError } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();

      // If we have a profile from Supabase, use it (source of truth)
      if (profile && !profileError) {
        // Set image preview if profile picture exists
        if (profile.profile_picture_url) {
          setImagePreview(profile.profile_picture_url);
        }

        const supabaseData = {
          name: profile.full_name || "",
          location: profile.location || "",
          profilePictureUrl: profile.profile_picture_url || "",
          skillTitle: profile.bio1 || "",
          bio: profile.bio2 || "",
          qualifications: profile.qualifications || "",
          experience: profile.experience || "",
          churchAffiliation: profile.church_name ? "affiliated" : "independent",
          churchName: profile.church_name || "",
          churchCity: profile.church_city || "",
          denomination: profile.denomination || "",
          churchRole: profile.church_role || "",
          waysToHelp: profile.ways_to_help || [],
          refereeName: profile.referee_name || "",
          refereeEmail: profile.referee_email || "",
          refereePhone: profile.referee_phone || "",
          refereeRole: profile.referee_role || "",
        };

        // Only update if Supabase has data (at least a name)
        // This prevents overwriting localStorage with empty Supabase data for new users
        if (profile.full_name) {
          setFormData(supabaseData);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(supabaseData));
        }

        // If profile is already complete and NOT in edit mode, show celebration
        if (profile.profile_complete && !isEditMode) {
          setCurrentStep("celebration");
        }
        // If in edit mode, always stay on profile step
        if (isEditMode) {
          setCurrentStep("profile");
        }
      } else {
        // No profile in Supabase, keep localStorage data (already loaded in useState)

        // If we have imagePreview in formData from localStorage, set it
        const savedData = localStorage.getItem(STORAGE_KEY);
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            if (parsed.profilePictureUrl) {
              setImagePreview(parsed.profilePictureUrl);
            }
          } catch (e) {
            console.error("Error parsing saved form data:", e);
          }
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Error loading profile:", error);
      // Don't alert or redirect - just use localStorage data
      setLoading(false);
    }
  };

  const autoSave = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
          return;
      }

      setSaving(true);

      // Use upsert to handle both new and existing profiles
      const { data, error } = await supabase
        .from("user_profiles")
        .upsert(
          {
            user_id: user.id,
            full_name: formData.name || null,
            location: formData.location || null,
            profile_picture_url: formData.profilePictureUrl || null,
            bio1: formData.skillTitle || null,
            bio2: formData.bio || null,
            qualifications: formData.qualifications || null,
            experience: formData.experience || null,
            church_name:
              formData.churchAffiliation === "affiliated"
                ? formData.churchName
                : null,
            church_city:
              formData.churchAffiliation === "affiliated"
                ? formData.churchCity
                : null,
            denomination:
              formData.churchAffiliation === "affiliated"
                ? formData.denomination
                : null,
            church_role:
              formData.churchAffiliation === "affiliated"
                ? formData.churchRole
                : null,
            ways_to_help: formData.waysToHelp || [],
            referee_name: formData.refereeName || null,
            referee_email: formData.refereeEmail || null,
            referee_phone: formData.refereePhone || null,
            referee_role: formData.refereeRole || null,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: "user_id",
          }
        )
        .select();

      if (error) {
        console.error("Auto-save error:", error);
      } else {
        setLastSaved(new Date());
      }

      setSaving(false);
    } catch (error) {
      console.error("Auto-save error:", error);
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    try {
      setUploadingImage(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        alert("You must be logged in to upload images");
        return;
      }

      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `profile-pictures/${fileName}`;

      const { data, error } = await supabase.storage
        .from("profile-pictures")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) throw error;

      const {
        data: { publicUrl },
      } = supabase.storage.from("profile-pictures").getPublicUrl(filePath);

      setFormData((prev) => ({
        ...prev,
        profilePictureUrl: publicUrl,
      }));

      setImagePreview(publicUrl);
      setUploadingImage(false);
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image. Please try again.");
      setUploadingImage(false);
    }
  };

  const handleContinue = async () => {
    // Save before continuing
    await autoSave();

    if (currentStep === "profile") {
      if (!formData.name.trim()) {
        alert("Please enter your name");
        return;
      }
      setCurrentStep("qualifications");
    } else if (currentStep === "qualifications") {
      setCurrentStep("church");
    } else if (currentStep === "church") {
      setCurrentStep("waysToHelp");
    } else if (currentStep === "waysToHelp") {
      setCurrentStep("churchReference");
    } else if (currentStep === "churchReference") {
      // Validate reference fields
      if (!formData.refereeName?.trim() || !formData.refereeEmail?.trim() ||
          !formData.refereePhone?.trim() || !formData.refereeRole) {
        alert("Please fill in all reference fields");
        return;
      }
      await markProfileComplete();
      setCurrentStep("celebration");
    }
  };

  const markProfileComplete = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return;

      const referenceToken = crypto.randomUUID();

      const { error } = await supabase
        .from("user_profiles")
        .upsert(
          {
            user_id: user.id,
            full_name: formData.name || null,
            location: formData.location || null,
            profile_picture_url: formData.profilePictureUrl || null,
            bio1: formData.skillTitle || null,
            bio2: formData.bio || null,
            qualifications: formData.qualifications || null,
            experience: formData.experience || null,
            church_name:
              formData.churchAffiliation === "affiliated"
                ? formData.churchName
                : null,
            church_city:
              formData.churchAffiliation === "affiliated"
                ? formData.churchCity
                : null,
            denomination:
              formData.churchAffiliation === "affiliated"
                ? formData.denomination
                : null,
            church_role:
              formData.churchAffiliation === "affiliated"
                ? formData.churchRole
                : null,
            ways_to_help: formData.waysToHelp || [],
            referee_name: formData.refereeName || null,
            referee_email: formData.refereeEmail || null,
            referee_phone: formData.refereePhone || null,
            referee_role: formData.refereeRole || null,
            profile_complete: true,
            profile_visible: false,
            application_submitted: true,
            reference_status: "pending",
            reference_token: referenceToken,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: "user_id",
          }
        );

      if (error) {
        console.error("Error marking profile complete:", error);
        return;
      }

      console.log("Profile marked as complete, sending reference email");

      // Send verification email to referee
      const verificationLink = `${window.location.origin}/verify-reference/${referenceToken}`;
      const applicantName = formData.name || "A Collavo applicant";

      try {
        await supabase.functions.invoke("send-email", {
          body: {
            to: formData.refereeEmail,
            subject: `Church Reference Request for ${applicantName}`,
            html: `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Church Reference Request</title>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9; }
    .container { background-color: #ffffff; border-radius: 12px; padding: 32px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
    .header { text-align: center; margin-bottom: 24px; }
    .header h1 { color: #1a1a1a; font-size: 24px; margin: 0 0 8px 0; }
    .header p { color: #737373; font-size: 15px; margin: 0; }
    .info-box { background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; margin: 24px 0; }
    .info-box p { margin: 4px 0; font-size: 15px; color: #334155; }
    .info-box strong { color: #1a1a1a; }
    .cta-button { display: inline-block; background-color: #1a1a1a; color: #ffffff !important; text-decoration: none; padding: 14px 32px; border-radius: 8px; font-weight: 600; font-size: 16px; margin: 24px 0; }
    .footer { margin-top: 32px; padding-top: 20px; border-top: 1px solid #e5e5e5; text-align: center; font-size: 13px; color: #9ca3af; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Church Reference Request</h1>
      <p>from Collavo</p>
    </div>
    <p>Hi ${formData.refereeName},</p>
    <p><strong>${applicantName}</strong> has applied to share their skills and serve churches through Collavo, and has listed you as their church reference.</p>
    <div class="info-box">
      <p><strong>Applicant:</strong> ${applicantName}</p>
      ${formData.churchName ? `<p><strong>Church:</strong> ${formData.churchName}</p>` : ""}
      ${formData.location ? `<p><strong>Location:</strong> ${formData.location}</p>` : ""}
    </div>
    <p>By confirming this reference, you are verifying that you know this person and that they are part of your church community.</p>
    <div style="text-align: center;">
      <a href="${verificationLink}" class="cta-button">Confirm Reference</a>
    </div>
    <p style="font-size: 13px; color: #737373;">If you don't recognise this person or didn't expect this email, you can safely ignore it.</p>
    <div class="footer">
      <p>This is an automated message from Collavo. Please do not reply to this email.</p>
    </div>
  </div>
</body>
</html>`,
          },
        });
        console.log("Reference verification email sent");
      } catch (emailError) {
        console.error("Error sending reference email:", emailError);
      }
    } catch (error) {
      console.error("Error marking profile complete:", error);
    }
  };

  const handleBack = () => {
    if (currentStep === "qualifications") {
      setCurrentStep("profile");
    } else if (currentStep === "church") {
      setCurrentStep("qualifications");
    } else if (currentStep === "waysToHelp") {
      setCurrentStep("church");
    } else if (currentStep === "churchReference") {
      setCurrentStep("waysToHelp");
    } else if (currentStep === "celebration") {
      setCurrentStep("churchReference");
    }
  };

  const handleSaveAndExit = async () => {
    await autoSave();
    navigate("/explore");
  };

  const handleFinish = () => {
    // Clear localStorage since they've completed the flow
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STEP_STORAGE_KEY);
    navigate("/explore");
  };

  const handlePreviewProfile = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      window.open(`/profile/${user.id}?preview=true`, "_blank");
    }
  };

  // Calculate step number for indicator
  const getStepNumber = () => {
    const steps = ["profile", "qualifications", "church", "waysToHelp", "churchReference"];
    const index = steps.indexOf(currentStep);
    return index >= 0 ? index + 1 : null;
  };

  const getTotalSteps = () => 5;

  if (loading) {
    return (
      <FormContainer>
        <div style={{ textAlign: "center", padding: "48px" }}>
          <p style={{ color: "#737373" }}>Loading your profile...</p>
        </div>
      </FormContainer>
    );
  }

  // Render the appropriate step
  const renderStep = () => {
    switch (currentStep) {
      case "profile":
        return (
          <ProfileBasics
            formData={formData}
            onInputChange={handleInputChange}
            onImageUpload={handleImageUpload}
            imagePreview={imagePreview}
            uploadingImage={uploadingImage}
            onContinue={handleContinue}
            onBack={handleBack}
            onSaveAndExit={handleSaveAndExit}
            saving={saving}
            lastSaved={lastSaved}
          />
        );

      case "qualifications":
        return (
          <QualificationsExperience
            formData={formData}
            onInputChange={handleInputChange}
            onContinue={handleContinue}
            onBack={handleBack}
            onSaveAndExit={handleSaveAndExit}
            saving={saving}
            lastSaved={lastSaved}
          />
        );

      case "church":
        return (
          <ChurchAffiliation
            formData={formData}
            onInputChange={handleInputChange}
            onContinue={handleContinue}
            onBack={handleBack}
            onSaveAndExit={handleSaveAndExit}
            saving={saving}
            lastSaved={lastSaved}
          />
        );

      case "waysToHelp":
        return (
          <WaysToHelp
            formData={formData}
            onInputChange={handleInputChange}
            onContinue={handleContinue}
            onBack={handleBack}
            onSaveAndExit={handleSaveAndExit}
            saving={saving}
            lastSaved={lastSaved}
          />
        );

      case "churchReference":
        return (
          <ChurchReference
            formData={formData}
            onInputChange={handleInputChange}
            onContinue={handleContinue}
            onBack={handleBack}
            onSaveAndExit={handleSaveAndExit}
            saving={saving}
            lastSaved={lastSaved}
          />
        );

      case "celebration":
        return (
          <ProfileCelebration
            formData={formData}
            imagePreview={imagePreview}
            onPreviewProfile={handlePreviewProfile}
            onFinish={handleFinish}
          />
        );

      default:
        return null;
    }
  };

  return renderStep();
}
